import { useEffect, useMemo, useState } from 'react'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { AvatarImage } from 'components/common'
import { useStore } from 'hooks/useStore'
import { ManageClubType } from 'graphql/type'
import { CLUB_PERMISSIONS } from 'utils/constants'
import { hasClubPermission } from 'utils/permissions'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from 'utils/routes'

type ActionType = 'event' | 'offer' | 'achievement'

interface ChooseActionClubModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	onSelect: (club: ManageClubType) => void
	title?: string
	action: ActionType
}

const ACTION_PERMISSION_MAP: Record<ActionType, (typeof CLUB_PERMISSIONS)[keyof typeof CLUB_PERMISSIONS]> = {
	event: CLUB_PERMISSIONS.CAN_MANAGE_CLUB_EVENTS,
	offer: CLUB_PERMISSIONS.CAN_MANAGE_CLUB_OFFERS,
	achievement: CLUB_PERMISSIONS.CAN_MANAGE_CLUB_ACHIEVEMENTS,
}

export const ChooseActionClubModal = ({ open, setOpen, onSelect, title, action }: ChooseActionClubModalProps) => {
	const { myClubs } = useStore()
	const navigate = useNavigate()
	const [selectedClub, setSelectedClub] = useState<ManageClubType | null>(null)

	const actionLabel = useMemo(() => {
		switch (action) {
			case 'event':
				return 'Event'
			case 'offer':
				return 'Offer'
			case 'achievement':
				return 'Achievement'
			default:
				return 'Action'
		}
	}, [action])

	const actionPluralLabel = useMemo(() => {
		switch (action) {
			case 'event':
				return 'events'
			case 'offer':
				return 'offers'
			case 'achievement':
				return 'achievements'
			default:
				return 'actions'
		}
	}, [action])

	const modalTitle = useMemo(() => {
		if (title) {
			return title
		}

		return `Create ${actionLabel}`
	}, [actionLabel, title])

	const availableClubs = useMemo(() => {
		if (!myClubs) {
			return []
		}

		const permission = ACTION_PERMISSION_MAP[action]
		return myClubs.filter((club: ManageClubType) => hasClubPermission(club, permission))
	}, [action, myClubs])

	useEffect(() => {
		if (open && availableClubs.length === 1) {
			onSelect(availableClubs[0])
			setOpen(false)
		}
	}, [open, availableClubs, onSelect, setOpen])

	useEffect(() => {
		if (open && action === 'event' && availableClubs.length === 0) {
			navigate(`${ROUTES.REP_CLUBS.MANAGE.EVENTS.CREATE()}?tab=info`)
			setOpen(false)
		}
	}, [action, availableClubs.length, navigate, open, setOpen])

	useEffect(() => {
		if (!open) {
			setSelectedClub(null)
		}
	}, [open])

	const handleSelect = (club: ManageClubType) => {
		onSelect(club)
		setOpen(false)
	}

	if (open && (availableClubs.length === 1 || (availableClubs.length === 0 && action === 'event'))) {
		return null
	}

	return (
		<BaseModal open={open} setOpen={setOpen} title={modalTitle} size="md">
			{availableClubs.length === 0 && action !== 'event' ? (
				<>
					<p className="text-sm text-secondary-text mb-4">
						You don't currently have permission to create {actionPluralLabel} for any of your clubs. Contact
						your club administrator to request access.
					</p>
					<div className="mt-4 flex items-center justify-center">
						<button
							onClick={() => setOpen(false)}
							className="px-4 py-2 rounded-md border border-translucent-plus-border text-secondary-text hover:bg-secondary-surface/[0.4] transition-colors"
						>
							Close
						</button>
					</div>
				</>
			) : (
				<>
					<p className="text-sm text-secondary-text mb-4">
						Which club would you like to create this {actionLabel.toLowerCase()} for? Select from the clubs
						below.
					</p>
					<div className="space-y-2 max-h-80 overflow-y-auto">
						{availableClubs.map((club: ManageClubType) => {
							const isSelected = selectedClub?.clubId === club.clubId
							return (
								<button
									key={`action-club-${action}-${club.clubId}`}
									onClick={() => setSelectedClub(club)}
									className={`w-full p-3 text-left rounded-lg transition-colors border flex items-center gap-3 ${
										isSelected
											? 'bg-secondary-surface/[0.4] border-primary-border'
											: 'hover:bg-secondary-surface/[0.4] border-translucent-plus-border'
									}`}
								>
									{club.images && (
										<AvatarImage
											src={club.images.icon || club.images.background}
											alt={club.name}
											className="w-10 h-10 rounded-full object-cover flex-shrink-0"
										/>
									)}
									<div className="flex-1 min-w-0">
										<p className="font-medium text-primary-text">{club.name}</p>
										<p className="text-sm text-secondary-text truncate">{club.description}</p>
									</div>
								</button>
							)
						})}
					</div>
					<div className="mt-4 flex items-center justify-between">
						<button
							onClick={() => setOpen(false)}
							className="px-4 py-2 rounded-md border border-translucent-plus-border text-secondary-text hover:bg-secondary-surface/[0.4] transition-colors"
						>
							Cancel
						</button>
						<button
							onClick={() => selectedClub && handleSelect(selectedClub)}
							disabled={!selectedClub}
							className="px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							Select
						</button>
					</div>
				</>
			)}
		</BaseModal>
	)
}
